import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Info, Upload } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const formatDob = (raw) => {
  const d = raw.replace(/\D/g, "").slice(0, 8);
  if (d.length <= 2) return d;
  if (d.length <= 4) return `${d.slice(0, 2)}/${d.slice(2)}`;
  return `${d.slice(0, 2)}/${d.slice(2, 4)}/${d.slice(4)}`;
};

const dobError = (v) => {
  if (!v) return null;
  const m = v.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!m) return "Use the format MM/DD/YYYY.";
  const [, mm, dd, yyyy] = m.map(Number);
  const y = Number(m[3]);
  if (mm < 1 || mm > 12) return "Month should be between 01 and 12.";
  if (dd < 1 || dd > 31) return "Day should be between 01 and 31.";
  if (y < 1900 || y > new Date().getFullYear()) return "Please check the year.";
  return null;
};

const FACILITIES = [
  "St. Luke's Hospital",
  "Lehigh Valley Hospital",
  "Open MRI & Allentown Diagnostic Imaging",
  "Somewhere else",
];

const RECORD_TYPES = [
  "Lab results",
  "Other testing results",
  "X-ray or MRI reports",
  "X-ray or MRI images/films",
];

export default function RegistrationPreview() {
  const [step, setStep] = useState(1);
  const [dob, setDob] = useState("");
  const [recordsChoice, setRecordsChoice] = useState(null);
  const [facility, setFacility] = useState("");
  const [dateUnsure, setDateUnsure] = useState(false);
  const err = dobError(dob);

  return (
    <div className="min-h-screen bg-[var(--brand-bg)] text-left">
      <div className="bg-amber-100 border-b border-amber-300 px-4 py-2.5 text-center text-sm font-medium text-amber-900">
        Development preview. This page is not linked for patients and does not collect or send any
        information. Booking registration activates once the AthenaOne workflow is connected.
      </div>
      <div className="max-w-xl mx-auto px-4 py-10">
        <Link to="/menopause" className="inline-flex items-center gap-2 text-sm font-medium text-[var(--brand-muted)] hover:text-[var(--brand-navy)]">
          <ArrowLeft className="h-4 w-4" /> Back to campaign page
        </Link>
        <h1 className="mt-4 font-serif-brand text-3xl font-medium text-[var(--brand-navy)]">
          Book your visit
        </h1>
        <div className="mt-4 flex items-center gap-2" aria-label={`Step ${step} of 2`}>
          {[1, 2].map((s) => (
            <div key={s} className={`h-1.5 flex-1 ${s <= step ? "bg-[var(--brand-gold)]" : "bg-[var(--brand-gold-soft)]"}`} />
          ))}
        </div>

        {step === 1 && (
          <div data-testid="preview-patient-details" className="mt-8 bg-white border border-[var(--brand-gold-soft)] p-6 space-y-5">
            <div>
              <h2 className="font-serif-brand text-xl font-medium text-[var(--brand-navy)]">About you</h2>
              <p className="mt-1 text-sm text-[var(--brand-muted)]">
                We only ask for what the booking system needs. Required fields are marked *.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="pv-first">First name *</Label>
                <Input id="pv-first" autoComplete="given-name" className="mt-1.5" />
              </div>
              <div>
                <Label htmlFor="pv-last">Last name *</Label>
                <Input id="pv-last" autoComplete="family-name" className="mt-1.5" />
              </div>
            </div>
            <div>
              <Label htmlFor="pv-dob">Date of birth *</Label>
              <Input
                id="pv-dob"
                data-testid="preview-dob-input"
                inputMode="numeric"
                placeholder="MM/DD/YYYY"
                value={dob}
                onChange={(e) => setDob(formatDob(e.target.value))}
                aria-invalid={!!err}
                className="mt-1.5"
              />
              {err ? (
                <p className="mt-1 text-sm text-red-700">{err}</p>
              ) : (
                <p className="mt-1 text-sm text-[var(--brand-muted)]">Type it directly, for example 04/12/1972.</p>
              )}
            </div>
            <div>
              <Label htmlFor="pv-email">Email *</Label>
              <Input id="pv-email" type="email" inputMode="email" autoComplete="email" className="mt-1.5" />
            </div>
            <div>
              <Label htmlFor="pv-phone">Mobile phone *</Label>
              <Input id="pv-phone" type="tel" inputMode="tel" autoComplete="tel" className="mt-1.5" />
            </div>
            <p className="text-xs text-[var(--brand-muted)]">
              Final required fields will be confirmed against the connected AthenaOne booking
              workflow before this form goes live, so nothing is asked for twice.
            </p>
            <button
              data-testid="preview-details-continue"
              onClick={() => setStep(2)}
              className="w-full bg-[var(--brand-navy)] text-white text-lg font-semibold py-3.5 hover:bg-[var(--brand-navy-deep)] transition-colors"
            >
              Continue
            </button>
          </div>
        )}

        {step === 2 && (
          <div data-testid="preview-records-step" className="mt-8 bg-white border border-[var(--brand-gold-soft)] p-6 space-y-5">
            <div>
              <h2 className="font-serif-brand text-xl font-medium text-[var(--brand-navy)]">
                Have records you&rsquo;d like Dr. Crosson to review?
              </h2>
              <p className="mt-1 text-base text-[var(--brand-muted)]">
                If you have previous lab results, testing reports, or imaging, you can securely share
                them with the office. This step is optional.
              </p>
            </div>
            <div className="space-y-3">
              {[
                { v: "upload", label: "Upload records" },
                { v: "retrieve", label: "The office may be able to retrieve my imaging" },
                { v: "skip", label: "Skip for now" },
              ].map((o) => (
                <button
                  key={o.v}
                  data-testid={`preview-records-${o.v}`}
                  onClick={() => setRecordsChoice(o.v)}
                  aria-pressed={recordsChoice === o.v}
                  className={`w-full text-left px-4 py-3 border-2 text-base transition-colors ${
                    recordsChoice === o.v
                      ? "border-[var(--brand-navy)] bg-[var(--brand-cream)] font-semibold"
                      : "border-[var(--brand-gold-soft)] hover:border-[var(--brand-gold)]"
                  }`}
                >
                  {o.label}
                </button>
              ))}
            </div>

            {recordsChoice === "upload" && (
              <div className="space-y-3">
                <p className="text-sm font-semibold text-[var(--brand-navy)]">What do you have?</p>
                <ul className="space-y-2">
                  {RECORD_TYPES.map((t) => (
                    <li key={t} className="flex items-center justify-between gap-2 text-base">
                      <label className="flex items-center gap-2.5">
                        <input type="checkbox" disabled className="h-4 w-4 accent-[var(--brand-navy)]" />
                        {t}
                      </label>
                      {t === "X-ray or MRI images/films" && (
                        <Popover>
                          <PopoverTrigger asChild>
                            <button
                              data-testid="preview-imaging-info-button"
                              aria-label="Information about imaging access"
                              className="p-1 text-[var(--brand-gold)] hover:text-[var(--brand-navy)]"
                            >
                              <Info className="h-4 w-4" />
                            </button>
                          </PopoverTrigger>
                          <PopoverContent className="max-w-xs text-sm leading-relaxed bg-white">
                            Had your imaging done at St. Luke&rsquo;s Hospital, Lehigh Valley
                            Hospital, or Open MRI &amp; Allentown Diagnostic Imaging? All-In
                            Precision Wellness can access imaging from these facilities. Let us know
                            where and approximately when your imaging was done so the team can
                            locate it. Additional authorization may be needed.
                          </PopoverContent>
                        </Popover>
                      )}
                    </li>
                  ))}
                </ul>
                <p className="text-xs text-[var(--brand-muted)]">
                  Note: a written X-ray or MRI report is different from the scan images themselves.
                  Actual imaging files may need to be transferred separately; the office will explain
                  how.
                </p>
                <div className="border-2 border-dashed border-[var(--brand-gold-soft)] p-6 text-center bg-[var(--brand-bg)]">
                  <Upload className="h-6 w-6 mx-auto text-[var(--brand-muted)]" aria-hidden="true" />
                  <p className="mt-2 text-sm font-medium text-[var(--brand-navy)]">
                    Secure upload isn&rsquo;t enabled yet.
                  </p>
                  <p className="mt-1 text-sm text-[var(--brand-muted)]">
                    The office will provide instructions for securely sharing your records before
                    your visit. Uploading is never required to book.
                  </p>
                </div>
              </div>
            )}

            {recordsChoice === "retrieve" && (
              <div className="space-y-4">
                <div>
                  <Label htmlFor="pv-facility">Where was your imaging done?</Label>
                  <select
                    id="pv-facility"
                    data-testid="preview-facility-select"
                    value={facility}
                    onChange={(e) => setFacility(e.target.value)}
                    className="mt-1.5 w-full border border-input bg-white px-3 py-2 text-base"
                  >
                    <option value="">Select a facility</option>
                    {FACILITIES.map((f) => (
                      <option key={f} value={f}>{f}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label htmlFor="pv-imaging-date">Approximately when?</Label>
                  <Input id="pv-imaging-date" placeholder="For example, spring 2024" disabled={dateUnsure} className="mt-1.5" />
                  <label className="mt-2 flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={dateUnsure}
                      onChange={(e) => setDateUnsure(e.target.checked)}
                      className="h-4 w-4 accent-[var(--brand-navy)]"
                    />
                    I&rsquo;m not sure
                  </label>
                </div>
                <div>
                  <Label htmlFor="pv-body-area">Body area imaged</Label>
                  <Input id="pv-body-area" placeholder="For example, right knee" className="mt-1.5" />
                </div>
                <p className="text-xs text-[var(--brand-muted)]">
                  The office will confirm what authorization is needed and let you know once records
                  are located. Retrieval isn&rsquo;t complete until the office confirms it.
                </p>
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={() => setStep(1)}
                className="flex-1 border-2 border-[var(--brand-navy)] text-[var(--brand-navy)] font-semibold py-3 bg-white"
              >
                Back
              </button>
              <button
                data-testid="preview-records-finish"
                disabled
                title="Enabled when the secure receiving workflow is confirmed"
                className="flex-1 bg-[var(--brand-navy)] text-white font-semibold py-3 opacity-40 cursor-not-allowed"
              >
                Continue
              </button>
            </div>
            <p className="text-xs text-[var(--brand-muted)]">
              Collection is disabled in this preview. Nothing typed here is stored or sent.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
